import type { BossState, GameDelta, GameState, Tower } from "@mmotdg/shared";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { BossRenderer } from "./BossRenderer.js";
import { LaneScene } from "./LaneScene.js";
import { MapRenderer } from "./MapRenderer.js";
import { MobRenderer } from "./MobRenderer.js";
import { ProjectileRenderer } from "./ProjectileRenderer.js";
import { TowerRenderer } from "./TowerRenderer.js";

const BOSS_HUB_RADIUS = 4;
const BOSS_OUTER_RADIUS = 22;

export class GameRenderer {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly controls: OrbitControls;
  readonly raycaster = new THREE.Raycaster();
  readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  private mapRenderer: MapRenderer | null = null;
  private mobRenderer: MobRenderer | null = null;
  private towerRenderer: TowerRenderer | null = null;
  private projectileRenderer: ProjectileRenderer | null = null;
  private bossRenderer: BossRenderer | null = null;

  // Boss mode only: per-lane wrappers. When non-null, the single-scene
  // renderers above remain null and rendering is dispatched per-lane.
  private laneScenes: Map<string, LaneScene> | null = null;
  private laneByMob = new Map<string, string>();
  private laneByTower = new Map<string, string>();
  private laneByOwner = new Map<string, string>();

  private state: GameState | null = null;
  private prevStateSnapshot = new Map<string, { x: number; y: number }>();
  private interpAlpha = 0;
  private lastTickReceivedAt = 0;
  private animFrame = 0;
  private elapsedClock = new THREE.Clock();

  private shakeStart = 0;
  private shakeDuration = 0;
  private shakeIntensity = 0;
  private cameraBaseTarget = new THREE.Vector3();

  private currentUserId: string | null = null;
  spectatedLaneId: string | null = null;

  // Smooth camera tween between spectator targets.
  private camTween: {
    from: THREE.Vector3;
    to: THREE.Vector3;
    fromTarget: THREE.Vector3;
    toTarget: THREE.Vector3;
    start: number;
    duration: number;
  } | null = null;

  constructor(canvas: HTMLCanvasElement, currentUserId?: string) {
    this.currentUserId = currentUserId ?? null;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x0a0d14, 1);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 300);
    this.camera.position.set(20, 25, 20);
    this.camera.lookAt(0, 0, 0);

    if (!canvas.hasAttribute("tabindex")) canvas.setAttribute("tabindex", "0");
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.screenSpacePanning = false;
    this.controls.minPolarAngle = Math.PI * 0.15;
    this.controls.maxPolarAngle = Math.PI * 0.48;
    this.controls.minZoom = 0.4;
    this.controls.maxZoom = 3.0;
    this.controls.zoomSpeed = 1.1;
    this.controls.rotateSpeed = 0.8;
    this.controls.panSpeed = 1.0;
    this.controls.keyPanSpeed = 18;
    this.controls.mouseButtons = {
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.ROTATE,
    };
    this.controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    };
    this.controls.keys = {
      LEFT: "ArrowLeft",
      UP: "ArrowUp",
      RIGHT: "ArrowRight",
      BOTTOM: "ArrowDown",
    };
    this.controls.listenToKeyEvents(canvas);

    this.scene.fog = new THREE.Fog(0x0a0d14, 30, 80);

    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambient);

    const dirLight = new THREE.DirectionalLight(0xfff0d0, 1.0);
    dirLight.position.set(15, 25, 10);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(2048, 2048);
    dirLight.shadow.camera.left = -25;
    dirLight.shadow.camera.right = 25;
    dirLight.shadow.camera.top = 25;
    dirLight.shadow.camera.bottom = -25;
    dirLight.shadow.bias = -0.0005;
    this.scene.add(dirLight);

    const hemiLight = new THREE.HemisphereLight(0x6ee7ff, 0x1c2333, 0.4);
    this.scene.add(hemiLight);

    window.addEventListener("resize", this.onResize);
    this.onResize();
  }

  private onResize = (): void => {
    const aspect = window.innerWidth / window.innerHeight;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };

  initialize(state: GameState): void {
    this.state = state;
    const isBoss = state.mode === "boss_hunt" || state.mode === "boss_endurance";
    if (isBoss) {
      this.initializeBossMode(state);
      this.centerCameraOnHub();
    } else {
      this.mapRenderer = new MapRenderer(this.scene, state);
      this.mobRenderer = new MobRenderer(this.scene);
      this.towerRenderer = new TowerRenderer(this.scene);
      this.projectileRenderer = new ProjectileRenderer(this.scene);
      this.centerCameraOnMap(state);
    }
  }

  private initializeBossMode(state: GameState): void {
    this.laneScenes = new Map();
    const lanes = [...state.lanes].sort((a, b) => a.spawnCell.cy - b.spawnCell.cy);

    // Identify the user's lane so we can place them at the front of the
    // radial layout (their base at the bottom of the screen).
    const me = this.currentUserId
      ? state.players.find((p) => p.id === this.currentUserId)
      : null;
    const myLaneId = me?.laneId ?? null;
    const myIndex = myLaneId ? lanes.findIndex((l) => l.id === myLaneId) : 0;
    const seatOffset = myIndex >= 0 ? myIndex : 0;

    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i]!;
      const relativeSeat = (i - seatOffset + lanes.length) % lanes.length;
      const ls = new LaneScene(
        this.scene,
        state,
        lane,
        relativeSeat,
        lanes.length,
        BOSS_HUB_RADIUS,
        BOSS_OUTER_RADIUS,
      );
      this.laneScenes.set(lane.id, ls);
    }
    this.rebuildLaneOwnerIndex(state);

    // Initial sync of entities into the appropriate lane scenes
    this.dispatchSnapshotToLanes(state);

    this.bossRenderer = new BossRenderer(this.scene);
    // Compute each lane's spawn-cell world position so the boss chains land
    // exactly on the spawn rings.
    const spawnTargets: Array<{ x: number; z: number }> = [];
    for (const lane of state.lanes) {
      const ls = this.laneScenes.get(lane.id);
      if (!ls) continue;
      const w = ls.transform.flatToWorld(lane.spawnCell.cx + 0.5, lane.spawnCell.cy + 0.5);
      spawnTargets.push({ x: w.x, z: w.z });
    }
    this.bossRenderer.attachToHub(spawnTargets);
    this.bossRenderer.setState(state.bossState, state);
  }

  private rebuildLaneOwnerIndex(state: GameState): void {
    this.laneByOwner.clear();
    for (const lane of state.lanes) {
      if (lane.ownerId) this.laneByOwner.set(lane.ownerId, lane.id);
    }
  }

  private dispatchSnapshotToLanes(state: GameState): void {
    if (!this.laneScenes) return;
    this.laneByMob.clear();
    this.laneByTower.clear();
    for (const m of state.mobs) this.laneByMob.set(m.id, m.laneId);
    for (const t of state.towers) {
      const lid = this.laneByOwner.get(t.ownerId);
      if (lid) this.laneByTower.set(t.id, lid);
    }
    for (const [laneId, ls] of this.laneScenes) {
      ls.mobRenderer.sync(state.mobs.filter((m) => m.laneId === laneId));
      ls.towerRenderer.sync(state.towers.filter((t) => this.laneByOwner.get(t.ownerId) === laneId));
      const towerMap = new Map<string, Tower>();
      for (const t of state.towers) towerMap.set(t.id, t);
      ls.projectileRenderer.sync(
        state.projectiles.filter((p) => this.laneByTower.get(p.towerId) === laneId),
        towerMap,
      );
      // Grey out dead players' slices so spectators can see at a glance who's
      // been eliminated.
      const lane = state.lanes.find((l) => l.id === laneId);
      const owner = lane?.ownerId ? state.players.find((p) => p.id === lane.ownerId) : null;
      ls.setAlive(owner ? owner.isAlive : true);
    }
  }

  applyBossState(boss: BossState | null): void {
    if (!this.bossRenderer || !this.state) return;
    this.bossRenderer.setState(boss, this.state);
  }

  applyBossEffect(kind: string | null): void {
    this.bossRenderer?.applyEffectAura(kind);
  }

  private renderLaneScenes(dt: number, t: number): void {
    if (!this.state || !this.laneScenes) return;
    const towerMap = new Map<string, Tower>();
    for (const tw of this.state.towers) towerMap.set(tw.id, tw);
    for (const [laneId, ls] of this.laneScenes) {
      const mobsHere = this.state.mobs.filter((m) => m.laneId === laneId);
      const towersHere = this.state.towers.filter((tw) => this.laneByOwner.get(tw.ownerId) === laneId);
      const projHere = this.state.projectiles.filter((p) => this.laneByTower.get(p.towerId) === laneId);
      ls.mobRenderer.render(mobsHere, this.prevStateSnapshot, this.interpAlpha, this.camera, dt);
      ls.projectileRenderer.render(projHere, dt);
      ls.towerRenderer.render(towersHere, dt, t);
      ls.mapRenderer.tick(t, dt);
    }
  }

  // Spectator mode: reposition the camera so that the given lane fills the
  // foreground. Used when the active player is dead and cycles through other
  // players to spectate. Animates with an ease-in-out tween between targets
  // instead of jumping.
  spectateLane(laneId: string | null): string | null {
    if (!this.laneScenes) return null;
    if (!laneId) {
      this.spectatedLaneId = null;
      // Tween back to the hub view.
      this.startCameraTween(new THREE.Vector3(0, 32, 40), new THREE.Vector3(0, 0, 0), 600);
      return null;
    }
    const ls = this.laneScenes.get(laneId);
    if (!ls) return null;
    this.spectatedLaneId = laneId;
    const angle = ls.transform.sliceCenterAngle;
    const radius = 40;
    const height = 32;
    const toPos = new THREE.Vector3(
      Math.cos(angle) * radius,
      height,
      Math.sin(angle) * radius,
    );
    const toTgt = new THREE.Vector3(0, 0, 0);
    this.startCameraTween(toPos, toTgt, 600);
    return laneId;
  }

  private startCameraTween(toPos: THREE.Vector3, toTarget: THREE.Vector3, durationMs: number): void {
    this.camTween = {
      from: this.camera.position.clone(),
      to: toPos.clone(),
      fromTarget: this.controls.target.clone(),
      toTarget: toTarget.clone(),
      start: performance.now(),
      duration: durationMs,
    };
    this.cameraBaseTarget.copy(toTarget);
    // Lock controls until the tween settles so user input doesn't fight us.
    this.controls.enabled = false;
  }

  private tickCameraTween(now: number): void {
    if (!this.camTween) return;
    const elapsed = now - this.camTween.start;
    const tRaw = Math.min(1, elapsed / this.camTween.duration);
    // Smooth cubic ease-in-out.
    const t = tRaw < 0.5
      ? 4 * tRaw * tRaw * tRaw
      : 1 - Math.pow(-2 * tRaw + 2, 3) / 2;
    this.camera.position.lerpVectors(this.camTween.from, this.camTween.to, t);
    this.controls.target.lerpVectors(this.camTween.fromTarget, this.camTween.toTarget, t);
    if (tRaw >= 1) {
      this.camera.position.copy(this.camTween.to);
      this.controls.target.copy(this.camTween.toTarget);
      this.camTween = null;
      this.controls.enabled = true;
    }
  }

  // Cycle the spectator target to the next/previous lane belonging to an
  // alive player (in radial seat order). Returns the new target lane id.
  cycleSpectator(direction: 1 | -1, state: GameState): string | null {
    if (!this.laneScenes) return null;
    const laneIds = [...this.laneScenes.keys()];
    if (laneIds.length === 0) return null;

    // Start from the currently spectated lane, or fall back to the user's
    // lane. The previous code had a precedence bug — `a ?? b ? x : y` parses
    // as `(a ?? b) ? x : y`, so currentId was *always* the user's lane, never
    // the spectated one. That made the cycle oscillate between user+1 and
    // user-1 forever, ignoring every other lane.
    let currentId: string | null = this.spectatedLaneId;
    if (!currentId && this.currentUserId) {
      currentId = state.players.find((p) => p.id === this.currentUserId)?.laneId ?? null;
    }
    let idx = currentId ? laneIds.indexOf(currentId) : -1;
    if (idx < 0) idx = 0;

    // Try each next lane in direction until we find an alive owner.
    for (let step = 1; step <= laneIds.length; step++) {
      const nextIdx = (idx + direction * step + laneIds.length) % laneIds.length;
      const nextId = laneIds[nextIdx]!;
      const lane = state.lanes.find((l) => l.id === nextId);
      if (!lane?.ownerId) continue;
      const owner = state.players.find((p) => p.id === lane.ownerId);
      if (!owner || !owner.isAlive) continue;
      return this.spectateLane(nextId);
    }
    return null;
  }

  private centerCameraOnHub(): void {
    // Boss mode: perspective camera tilted south-south-east, so the user's
    // slice (south, +Z) fills the foreground and the opposing slices recede
    // with proper perspective.
    this.camera.fov = 50;
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.camera.position.set(0, 32, 40);
    this.controls.target.set(0, 0, 0);
    this.cameraBaseTarget.copy(this.controls.target);
    this.controls.update();
  }

  private centerCameraOnMap(state: GameState): void {
    const cx = state.gridWidth / 2;
    const cy = state.gridHeight / 2;
    this.camera.position.set(cx + 18, 28, cy + 18);
    this.controls.target.set(cx, 0, cy);
    this.cameraBaseTarget.copy(this.controls.target);
    this.controls.update();
  }

  resetView(): void {
    if (this.state) this.centerCameraOnMap(this.state);
  }

  applySnapshot(state: GameState): void {
    this.state = state;
    if (this.mapRenderer) this.mapRenderer.updatePaths(state);
    if (this.towerRenderer) this.towerRenderer.sync(state.towers);
    if (this.mobRenderer) this.mobRenderer.sync(state.mobs);
    this.recordPrevPositions();
  }

  applyDelta(delta: GameDelta, state: GameState): void {
    this.recordPrevPositions();
    this.state = state;

    const towersChanged = delta.towersAdded.length > 0 || delta.towersRemoved.length > 0 || delta.towersUpdated.length > 0;
    const mobsChanged = delta.mobsAdded.length > 0 || delta.mobsRemoved.length > 0;
    const projectilesChanged = delta.projectilesAdded.length > 0 || delta.projectilesRemoved.length > 0;
    const pathsChanged = delta.lanesUpdated.some((l) => l.path !== null);

    if (this.laneScenes) {
      this.rebuildLaneOwnerIndex(state);
      this.dispatchSnapshotToLanes(state);
      if (pathsChanged) {
        for (const ls of this.laneScenes.values()) ls.mapRenderer.updatePaths(state);
      }
    } else {
      if (towersChanged && this.towerRenderer) this.towerRenderer.sync(state.towers);
      if (mobsChanged && this.mobRenderer) this.mobRenderer.sync(state.mobs);
      if (projectilesChanged && this.projectileRenderer) {
        this.projectileRenderer.sync(state.projectiles, this.towersById(state.towers));
      }
      if (pathsChanged && this.mapRenderer) this.mapRenderer.updatePaths(state);
    }

    this.lastTickReceivedAt = performance.now();
  }

  flashMobDamage(mobId: string): void {
    if (this.laneScenes) {
      const laneId = this.laneByMob.get(mobId);
      if (laneId) this.laneScenes.get(laneId)?.mobRenderer.flashDamage(mobId);
      return;
    }
    this.mobRenderer?.flashDamage(mobId);
  }

  private laneByCy(cy: number): string | null {
    if (!this.laneScenes) return null;
    for (const [id, ls] of this.laneScenes) {
      if (cy >= ls.yMin && cy <= ls.yMax) return id;
    }
    return null;
  }

  syncEntities(): void {
    if (!this.state) return;
    if (this.towerRenderer) this.towerRenderer.sync(this.state.towers);
    if (this.mobRenderer) this.mobRenderer.sync(this.state.mobs);
    if (this.projectileRenderer) {
      this.projectileRenderer.sync(this.state.projectiles, this.towersById(this.state.towers));
    }
    if (this.mapRenderer) this.mapRenderer.updatePaths(this.state);
  }

  private towersById(towers: Tower[]): Map<string, Tower> {
    const map = new Map<string, Tower>();
    for (const t of towers) map.set(t.id, t);
    return map;
  }

  private recordPrevPositions(): void {
    if (!this.state) return;
    this.prevStateSnapshot.clear();
    for (const m of this.state.mobs) {
      this.prevStateSnapshot.set(m.id, { x: m.position.x, y: m.position.y });
    }
  }

  start(): void {
    this.elapsedClock.start();
    const loop = () => {
      this.animFrame = requestAnimationFrame(loop);
      const now = performance.now();
      const elapsed = now - this.lastTickReceivedAt;
      this.interpAlpha = Math.min(1, elapsed / 50);
      const dt = this.elapsedClock.getDelta();
      const t = this.elapsedClock.elapsedTime;

      if (this.state) {
        if (this.laneScenes) {
          this.renderLaneScenes(dt, t);
        } else {
          if (this.mobRenderer) {
            this.mobRenderer.render(this.state.mobs, this.prevStateSnapshot, this.interpAlpha, this.camera, dt);
          }
          if (this.projectileRenderer) {
            this.projectileRenderer.render(this.state.projectiles, dt);
          }
          if (this.towerRenderer) {
            this.towerRenderer.render(this.state.towers, dt, t);
          }
          if (this.mapRenderer) this.mapRenderer.tick(t, dt);
        }
      }
      if (this.bossRenderer) this.bossRenderer.tick(t, dt);

      this.applyShake(now);
      this.tickCameraTween(now);

      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    loop();
  }

  private applyShake(now: number): void {
    if (this.shakeDuration <= 0) return;
    const elapsed = now - this.shakeStart;
    if (elapsed >= this.shakeDuration) {
      this.shakeDuration = 0;
      this.controls.target.copy(this.cameraBaseTarget);
      return;
    }
    const fall = 1 - elapsed / this.shakeDuration;
    const mag = this.shakeIntensity * fall;
    const ox = (Math.random() - 0.5) * 2 * mag;
    const oz = (Math.random() - 0.5) * 2 * mag;
    this.controls.target.set(
      this.cameraBaseTarget.x + ox,
      this.cameraBaseTarget.y,
      this.cameraBaseTarget.z + oz,
    );
  }

  shake(intensity: number, durationMs: number): void {
    this.cameraBaseTarget.copy(this.controls.target);
    this.shakeStart = performance.now();
    this.shakeDuration = durationMs;
    this.shakeIntensity = intensity;
  }

  flashTower(towerId: string): void {
    if (this.laneScenes) {
      const laneId = this.laneByTower.get(towerId);
      if (laneId) this.laneScenes.get(laneId)?.towerRenderer.flash(towerId);
      return;
    }
    this.towerRenderer?.flash(towerId);
  }

  setSelectedTower(towerId: string | null): void {
    if (this.laneScenes) {
      // Clear all, set on the owning lane
      for (const ls of this.laneScenes.values()) ls.towerRenderer.setSelected(null);
      if (towerId) {
        const laneId = this.laneByTower.get(towerId);
        if (laneId) this.laneScenes.get(laneId)?.towerRenderer.setSelected(towerId);
      }
      return;
    }
    this.towerRenderer?.setSelected(towerId);
  }

  showRangePreview(target: { x: number; y: number; range: number } | null): void {
    if (this.laneScenes) {
      // Hide on all, show on the owning lane
      for (const ls of this.laneScenes.values()) ls.mapRenderer.showRangePreview(null);
      if (target) {
        const laneId = this.laneByCy(Math.floor(target.y));
        if (laneId) this.laneScenes.get(laneId)?.mapRenderer.showRangePreview(target);
      }
      return;
    }
    this.mapRenderer?.showRangePreview(target);
  }

  spawnHitSpark(x: number, y: number, splashRadius: number): void {
    if (this.laneScenes) {
      const laneId = this.laneByCy(Math.floor(y));
      if (laneId) this.laneScenes.get(laneId)?.mapRenderer.spawnHitSpark(x, y, splashRadius);
      return;
    }
    this.mapRenderer?.spawnHitSpark(x, y, splashRadius);
  }

  pulseMobSpawn(x: number, y: number, isFlying: boolean): void {
    if (this.laneScenes) {
      const laneId = this.laneByCy(Math.floor(y));
      if (laneId) this.laneScenes.get(laneId)?.mapRenderer.spawnPulse(x, y, isFlying ? 1.2 : 0.2);
      return;
    }
    this.mapRenderer?.spawnPulse(x, y, isFlying ? 1.2 : 0.2);
  }

  pulseBase(x: number, y: number): void {
    if (this.laneScenes) {
      const laneId = this.laneByCy(Math.floor(y));
      if (laneId) this.laneScenes.get(laneId)?.mapRenderer.basePulse(x, y);
      return;
    }
    this.mapRenderer?.basePulse(x, y);
  }

  // Project a flat (cx, cy) grid coord to screen coords with edge-clamping
  // info, so callers can render off-screen indicators (e.g. an arrow at the
  // viewport edge pointing toward a base behind the camera).
  projectFlatToViewport(flatX: number, flatY: number): {
    sx: number; sy: number; onScreen: boolean; behind: boolean;
    angleFromCenter: number; // radians, where 0 = right, π/2 = down
  } {
    let px = flatX;
    let pz = flatY;
    if (this.laneScenes) {
      const laneId = this.laneByCy(Math.floor(flatY));
      if (laneId) {
        const ls = this.laneScenes.get(laneId);
        if (ls) {
          const w = ls.transform.flatToWorld(flatX, flatY);
          px = w.x;
          pz = w.z;
        }
      }
    }
    const vec = new THREE.Vector3(px, 0.6, pz).project(this.camera);
    const canvas = this.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const behind = vec.z > 1;
    // When the point is behind the camera, project() returns sign-flipped NDC.
    // We need a stable direction vector for the arrow, so synthesize one from
    // the world-space delta between the camera-forward plane and the target.
    let ndcX = vec.x;
    let ndcY = vec.y;
    if (behind) { ndcX = -ndcX; ndcY = -ndcY; }
    const onScreen = !behind && Math.abs(ndcX) <= 1 && Math.abs(ndcY) <= 1;
    // Clamp to the unit square preserving direction, then map to pixel coords.
    let cx2 = ndcX;
    let cy2 = ndcY;
    if (!onScreen) {
      const m = Math.max(Math.abs(cx2), Math.abs(cy2), 0.0001);
      // Scale so the larger axis sits at 0.92 (leaves edge padding).
      const k = 0.92 / m;
      cx2 *= k;
      cy2 *= k;
    }
    const sx = (cx2 * 0.5 + 0.5) * rect.width + rect.left;
    const sy = (-cy2 * 0.5 + 0.5) * rect.height + rect.top;
    return {
      sx, sy, onScreen, behind,
      angleFromCenter: Math.atan2(-ndcY, ndcX),
    };
  }

  worldToScreen(worldX: number, worldY: number): { x: number; y: number } | null {
    // In boss mode the coords passed are FLAT grid coords (cx, cy). Run them
    // through the owning lane scene's polar transform to get the actual world
    // XZ. Outside boss mode it's already world.
    let px = worldX;
    let pz = worldY;
    if (this.laneScenes) {
      const laneId = this.laneByCy(Math.floor(worldY));
      if (laneId) {
        const ls = this.laneScenes.get(laneId);
        if (ls) {
          const w = ls.transform.flatToWorld(worldX, worldY);
          px = w.x;
          pz = w.z;
        }
      }
    }
    const vec = new THREE.Vector3(px, 0.6, pz).project(this.camera);
    const canvas = this.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const sx = (vec.x * 0.5 + 0.5) * rect.width + rect.left;
    const sy = (-vec.y * 0.5 + 0.5) * rect.height + rect.top;
    if (vec.z > 1 || vec.z < -1) return null;
    return { x: sx, y: sy };
  }

  stop(): void {
    cancelAnimationFrame(this.animFrame);
  }

  pickGridCell(ndc: { x: number; y: number }): { cx: number; cy: number } | null {
    this.raycaster.setFromCamera(new THREE.Vector2(ndc.x, ndc.y), this.camera);
    const intersection = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.groundPlane, intersection)) return null;
    if (this.laneScenes) {
      for (const ls of this.laneScenes.values()) {
        const cell = ls.worldToCell(intersection.clone());
        if (cell) return cell;
      }
      return null;
    }
    return { cx: Math.floor(intersection.x), cy: Math.floor(intersection.z) };
  }

  highlightCell(cell: { cx: number; cy: number } | null, valid: boolean): void {
    if (this.laneScenes) {
      for (const ls of this.laneScenes.values()) ls.mapRenderer.highlightCell(null, valid);
      if (cell) {
        const laneId = this.laneByCy(cell.cy);
        if (laneId) this.laneScenes.get(laneId)?.mapRenderer.highlightCell(cell, valid);
      }
      return;
    }
    this.mapRenderer?.highlightCell(cell, valid);
  }

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    this.stop();
    this.projectileRenderer?.dispose();
    this.towerRenderer?.dispose();
    this.mobRenderer?.dispose();
    this.mapRenderer?.dispose();
    this.bossRenderer?.dispose();
    if (this.laneScenes) {
      for (const ls of this.laneScenes.values()) ls.dispose();
      this.laneScenes = null;
    }
    this.laneByMob.clear();
    this.laneByTower.clear();
    this.laneByOwner.clear();
    this.projectileRenderer = null;
    this.towerRenderer = null;
    this.mobRenderer = null;
    this.mapRenderer = null;
    this.bossRenderer = null;
    this.state = null;
    this.prevStateSnapshot.clear();
    this.controls.dispose();
    this.renderer.dispose();
  }
}
